import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getCsrfTokenFromCookie, CSRF_HEADER_NAME, refreshCsrfToken } from "./csrf";
import { addBreadcrumb, captureError } from "./sentry";
import { apm } from "./apm";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * Get headers with CSRF token for state-changing requests
 */
function getRequestHeaders(method: string, hasData: boolean): HeadersInit {
  const headers: Record<string, string> = {};

  if (hasData) {
    headers["Content-Type"] = "application/json";
  }

  // Add CSRF token for state-changing methods
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method.toUpperCase())) {
    const csrfToken = getCsrfTokenFromCookie();
    if (csrfToken) {
      headers[CSRF_HEADER_NAME] = csrfToken;
    }
  }

  return headers;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  // Add breadcrumb for API request
  addBreadcrumb('api', `${method} ${url}`, { data: data ? 'present' : 'none' }, 'info');

  const headers = getRequestHeaders(method, !!data);
  const startTime = performance.now();

  try {
    let res = await fetch(url, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });

    // If we get a 403 with CSRF error, try refreshing the token and retry once
    if (res.status === 403) {
      try {
        const errorData = await res.clone().json();
        if (errorData.error && errorData.error.includes("CSRF")) {
          addBreadcrumb('api', 'CSRF token refresh attempt', {}, 'info');
          const newToken = await refreshCsrfToken();
          if (newToken) {
            const retryHeaders = { ...headers } as Record<string, string>;
            retryHeaders[CSRF_HEADER_NAME] = newToken;
            res = await fetch(url, {
              method,
              headers: retryHeaders,
              body: data ? JSON.stringify(data) : undefined,
              credentials: "include",
            });
          }
        }
      } catch {
        // Ignore JSON parse errors, return original response
      }
    }

    // Record APM metrics
    const duration = performance.now() - startTime;
    apm.recordApiCall(url, method, duration, res.ok, res.status);

    // Extract trace ID from response for correlation
    const traceId = res.headers.get('X-Trace-Id');
    if (traceId) {
      apm.setTraceId(traceId);
    }

    // Add breadcrumb for response
    addBreadcrumb('api', `${method} ${url} response`, { status: res.status }, res.ok ? 'info' : 'error');

    await throwIfResNotOk(res);
    return res;
  } catch (error) {
    // Record failed API call
    const duration = performance.now() - startTime;
    apm.recordApiCall(url, method, duration, false);

    // Capture API errors with context
    if (error instanceof Error) {
      captureError(error, { url, method, data: data ? JSON.stringify(data).slice(0, 200) : undefined });
    }
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
